import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GoogleGroupTypes, GroupingAsset } from '@cdw-ae/common-data';
import { TreeNode } from '@cdw-ae/common-ui-components-tree';
import { SelectedFilterField } from '@cdw-ae/filter-bar-interfaces';
import { getQueryStringFromParams } from '@cdw-ae/routes-shared-api';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiUrlBuilderService } from '../../../../../../common/services/api-url-builder.service';

/** Flattened grouping asset, reduced to the fields the OU tree needs. */
interface OrgUnitSummary {
  groupingAssetId: string;
  orgUnitId: string;
  orgUnitPath: string;
  /** Set by the grouping sync; absent on rows written outside of it. */
  lastAssetSync?: Date;
}

@Injectable({
  providedIn: 'root',
})
export class OrgUnitsDataService {
  /** Maps OU path → GroupingAsset UUID for save-time conversion */
  public pathToUuidMap = new Map<string, string>();

  constructor(
    private httpClient: HttpClient,
    private readonly urlBuilder: ApiUrlBuilderService
  ) {}

  public getOrgUnits(args: {
    startRow: number,
    endRow: number,
    activeFilters?: SelectedFilterField[],
    selectFields?: string[],
  } = {
    startRow: 0,
    endRow: 100000,
  }): Observable<TreeNode[]> {
    const activeFilters = args.activeFilters || [];
    activeFilters.push({
      field: {
        displayName: 'Type',
        name: 'vendorProperties.google.type',
      },
      selectedMatcher: {
        name: 'equals',
      },
      matchValue: GoogleGroupTypes.ORG_UNIT,
    } as unknown as SelectedFilterField);
    const paramsQueryString = getQueryStringFromParams({ ...args, activeFilters });

    return this.httpClient
      .get<{ data: GroupingAsset[] }>(
        `${this.urlBuilder.backendApi('grouping-assets', '')}?${paramsQueryString}`
      )
      .pipe(
        map((response) => {
          // Filter to only OrgUnits
          const items = response.data ?? [];
          const orgUnits = items
            .map(g => ({
              groupingAssetId: g.id,
              orgUnitId: g.vendorId ?? '',
              orgUnitPath: g.value ?? '',
              lastAssetSync: g.lastAssetSync,
            }));

          const uniqueOrgUnits = this.dedupeByPath(orgUnits);

          this.pathToUuidMap.clear();
          for (const ou of uniqueOrgUnits) {
            this.pathToUuidMap.set(ou.orgUnitPath, ou.groupingAssetId);
          }

          return this.buildOrgUnitTree(uniqueOrgUnits);
        })
      );
  }

  /**
   * Collapse grouping assets that share an OU path down to one row each.
   *
   * A single path can legitimately map to several asset_groupings rows: that
   * table is unique on (customerId, vendorId, vendor), not on value. A
   * placeholder root OU written at onboarding and the real root written later
   * by the grouping sync therefore both live under "/". Left alone, the pair
   * renders the entire subtree twice (EDUS-2748) and turns pathToUuidMap into
   * a coin flip between the two UUIDs — which matters because designations and
   * OU collections are saved against whichever UUID wins.
   *
   * Rows carrying a lastAssetSync win: those came from the grouping sync and
   * hold the vendor's real ID. Ties keep the first row seen, so the result is
   * stable no matter what order the server returns.
   */
  private dedupeByPath(orgUnits: OrgUnitSummary[]): OrgUnitSummary[] {
    const byPath = new Map<string, OrgUnitSummary>();

    for (const ou of orgUnits) {
      const existing = byPath.get(ou.orgUnitPath);
      if (!existing || (!existing.lastAssetSync && ou.lastAssetSync)) {
        // Map.set on an existing key keeps its original position, so replacing
        // a placeholder with the synced row preserves first-seen ordering.
        byPath.set(ou.orgUnitPath, ou);
      }
    }

    return Array.from(byPath.values());
  }

  /**
   * Build a hierarchical tree structure from flat org unit list.
   * Uses a two-pass algorithm with a Map to avoid O(n²) parent lookups.
   * Each node represents an OU with displayName set to its full path.
   *
   * Expects one entry per path — see dedupeByPath(). Duplicate paths would
   * push the same node into the tree once per row.
   */
  private buildOrgUnitTree(orgUnits: OrgUnitSummary[]): TreeNode[] {
    const nodeMap = new Map<string, TreeNode>();
    const roots: TreeNode[] = [];

    // First pass: create all nodes
    for (const ou of orgUnits) {
      const node: TreeNode = {
        displayName: ou.orgUnitPath === '/'
          ? '/'
          : (ou.orgUnitPath.split('/').pop() ?? ou.orgUnitPath),
        value: ou.orgUnitPath,// ou.orgUnitId, // Maybe we need the id in the future, but at the moment sending the update request with the org unit path is the way we are set up
        children: [],
      };
      nodeMap.set(ou.orgUnitPath, node);
    }

    // Second pass: build parent-child relationships
    for (const ou of orgUnits) {
      const node = nodeMap.get(ou.orgUnitPath);
      if (!node) continue;

      if (ou.orgUnitPath === '/') {
        roots.push(node);
      } else {
        const parentPath = ou.orgUnitPath.substring(0, ou.orgUnitPath.lastIndexOf('/')) || '/';
        const parent = nodeMap.get(parentPath);
        if (parent?.children) {
          parent.children.push(node);
        } else {
          roots.push(node);
        }
      }
    }

    return roots;
  }

}
